import React, { useCallback, useEffect, useRef, useState } from "react";
import { Share as ShareIcon } from "@mui/icons-material";
import { Box, Button, Toolbar, Tooltip } from "@mui/material";
import { IRowNode, PostSortRowsParams, SelectionChangedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import analytics from "fond/analytics";
import { DisplayPageSize } from "fond/constants";
import Share from "fond/share/Share";
import { Folder, Project, RecentlyViewedEntity, ResourceEntity } from "fond/types";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { AgGrid, BlockSpinner } from "fond/widgets";

import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import { EntityMenu } from "../ItemMenu/EntityMenu";
import useProjectsTable from "../useProjectsTable";

import ProjectListToolbar from "./ProjectListToolbar";

interface IProps {
  isLoading?: boolean;
  items: ResourceEntity[] | RecentlyViewedEntity<ResourceEntity>[] | null;
  folder?: Folder;
  searchText: string;
}

const LoadingOverlay = () => <BlockSpinner />;

const ProjectList: React.FC<IProps> = ({ folder, isLoading = false, items, searchText }: IProps) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<Element | undefined>(undefined);
  const [menuEntity, setMenuEntity] = useState<ResourceEntity | undefined>(undefined);
  const [selectedItems, setSelectedItems] = useState<Array<Folder | Project>>([]);
  const gridRef = useRef<AgGridReact>(null);

  useEffect(() => {
    gridRef.current?.api?.setGridOption("quickFilterText", searchText);
  }, [searchText]);

  useEffect(() => {
    /*
     * This event is being captured here instead in a parent component as we are interested in the size of
     * the project list being rendered for performance purposes. We may change this in future.
     */
    if (items?.length === undefined) {
      return;
    }
    analytics.track("Entered project list page", { projectCount: items?.length });
  }, [items?.length]);

  const toggleShareModal = () => {
    setShowShareModal(!showShareModal);
  };

  /**
   * Callback function called when row selection changes within the grid.
   */
  const onSelectionChanged = (event: SelectionChangedEvent) => {
    const selectedNodes = event.api.getSelectedNodes();
    const selectedData: Array<Folder | Project> = [];
    selectedNodes.forEach((node) => {
      if (node.data) selectedData.push(node.data);
    });
    setSelectedItems(selectedData);
  };

  /**
   * We always keep the Folder entity type at the top of the grid.
   * Note: We exclude MultiProjects from this sorting, even though they are a type of folder.
   */
  const postSortRows = (params: PostSortRowsParams<Folder | Project>) => {
    let rowNodes = params.nodes;
    // here we put Folder rows on top while preserving the sort order
    let nextInsertPos = 0;
    for (let i = 0; i < rowNodes.length; i += 1) {
      const type = rowNodes[i].data?.EntityType;
      const isMultiProject = type === "folder" && (rowNodes[i].data as Folder | undefined)?.MultiProject;
      if (type === "folder" && !isMultiProject) {
        rowNodes.splice(nextInsertPos, 0, rowNodes.splice(i, 1)[0]);
        nextInsertPos += 1;
      }
    }
  };

  const isRowSelectable = (rowNode: IRowNode<Folder | Project>): boolean => {
    return (
      (rowNode.data?.EntityType !== "folder" && !rowNode.data?.MultiProjectArea) ||
      (rowNode.data?.EntityType === "folder" && !!rowNode.data.MultiProject)
    );
  };

  /**
   * Callback function that handles opening the row menu
   */
  const openRowMenu = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>, entity?: ResourceEntity) => {
    setMenuAnchor(e.currentTarget);
    setMenuEntity(entity);
  }, []);

  const { columns, id } = useProjectsTable({ openRowMenu });

  /**
   * Callback function that handles closing the row menu
   */
  const closeRowMenu = () => setMenuAnchor(undefined);

  const handleOnClear = () => {
    gridRef.current?.api?.deselectAll();
    setSelectedItems([]);
  };

  return (
    <>
      <Box className="project-list" data-testid="project-list" sx={{ display: "flex", flexGrow: 1, flexDirection: "column" }}>
        <Toolbar>
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            {selectedItems.length > 0 ? (
              <ProjectListToolbar selectedItems={selectedItems} onComplete={handleOnClear} onClear={handleOnClear} />
            ) : (
              <>
                <Breadcrumbs />
                {folder?.ID && permissionCheck(folder.Permission.Level, Actions.FOLDER_SHARE_VIEW) && (
                  <Tooltip title={`Share ${folder.Name}`}>
                    <Button
                      data-testid="share-button"
                      onClick={toggleShareModal}
                      size="small"
                      color="primary"
                      variant="outlined"
                      startIcon={<ShareIcon />}
                    >
                      Share
                    </Button>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        </Toolbar>

        <AgGrid
          id={id}
          ref={gridRef}
          containerProps={{ flexGrow: 1 }}
          rowData={items}
          gridOptions={{
            defaultColDef: {
              resizable: true,
              sortable: true,
            },
            domLayout: "autoHeight",
            rowClass: "no-alternate-background",
            animateRows: true,
            suppressGroupRowsSticky: true,
            suppressMenuHide: false,
            rowSelection: folder?.MultiProject
              ? undefined
              : {
                  mode: "multiRow",
                  checkboxes: true,
                  isRowSelectable,
                  hideDisabledCheckboxes: true,
                  selectAll: "currentPage",
                },
            suppressContextMenu: true,
            sideBar: false,
            rowGroupPanelShow: "never",
            pagination: true,
            paginationPageSize: DisplayPageSize.LARGE,
            postSortRows,
            loadingOverlayComponent: LoadingOverlay,
            getRowId: (params) => params.data.ID,
          }}
          columnDefs={columns}
          onSelectionChanged={onSelectionChanged}
          loading={isLoading}
          size="large"
        />
      </Box>
      {showShareModal && folder?.ID && <Share resource={folder} onClose={() => toggleShareModal()} />}
      {(menuAnchor || menuEntity) && <EntityMenu entity={menuEntity} anchorEl={menuAnchor} onMenuClose={closeRowMenu} source="entity-list" />}
    </>
  );
};

export default ProjectList;
